import React, { useState, useEffect } from 'react';
import { setCookie, getCookie } from '../utils/cookieUtils';
import { useAuth } from '../AuthContext';

const AddToHomeScreenPrompt = ({ conversations = {} }) => {
  const { isAuthenticated } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    const checkShouldShow = () => {
      // Must be authenticated
      if (!isAuthenticated) return false;

      // Must have at least 2 conversations with messages
      const conversationList = Object.values(conversations);
      const conversationsWithMessages = conversationList.filter(conv => 
        (conv.messageCount && conv.messageCount >= 2) || 
        (conv.messages && conv.messages.length >= 2)
      );
      
      if (conversationsWithMessages.length < 2) return false;

      // Don't show if user said "No thanks"
      const noThanks = getCookie('addToHomeScreen_noThanks');
      if (noThanks === 'true') return false;

      // Check if user said "Maybe later" and if 24 hours have passed
      const maybeLater = getCookie('addToHomeScreen_maybeLater');
      if (maybeLater) {
        const laterTime = parseInt(maybeLater);
        const now = Date.now();
        if (now < laterTime) return false; // Still within 24 hours
      }

      // Don't show on desktop
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (!isMobile) return false;

      // Don't show if already installed (standalone mode)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true;
      if (isStandalone) return false;

      return true;
    };

    // Show prompt after a short delay
    const timer = setTimeout(() => {
      if (checkShouldShow()) {
        setShowPrompt(true);
      }
    }, 3000); // Show after 3 seconds

    return () => clearTimeout(timer);
  }, [isAuthenticated, conversations]);

  const handleNoThanks = () => {
    setCookie('addToHomeScreen_noThanks', 'true', 365); // Don't ask for a year
    setShowPrompt(false);
  };

  const handleMaybeLater = () => {
    const tomorrow = Date.now() + (24 * 60 * 60 * 1000); // 24 hours from now
    setCookie('addToHomeScreen_maybeLater', tomorrow.toString(), 1);
    setShowPrompt(false);
  };

  const handleYes = () => {
    setShowPrompt(false);
    setShowInstructions(true);
  };

  const closeInstructions = () => {
    setShowInstructions(false);
    setCookie('addToHomeScreen_noThanks', 'true', 365); // Don't ask again
  };

  const getInstructions = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isSamsung = /SamsungBrowser/.test(navigator.userAgent);
    const isChrome = /Chrome/.test(navigator.userAgent) && !/SamsungBrowser/.test(navigator.userAgent);

    if (isIOS) {
      return {
        title: "Add to Home Screen - iOS",
        steps: [
          "1. Open this website in Safari (not Chrome or other browsers)",
          "2. Tap the Share button (square with arrow pointing up) at the bottom",
          "3. Scroll down and tap 'Add to Home Screen'",
          "4. Tap 'Add' in the top right corner"
        ],
        note: "Note: This only works in Safari on iOS devices."
      };
    } else if (isAndroid) {
      if (isSamsung) {
        return {
          title: "Add to Home Screen - Samsung Internet",
          steps: [
            "1. Tap the menu button (three lines) at the bottom",
            "2. Tap 'Add page to' or 'Add to Home screen'",
            "3. Choose 'Home screen'",
            "4. Tap 'Add'"
          ]
        };
      } else if (isChrome) {
        return {
          title: "Add to Home Screen - Chrome",
          steps: [
            "1. Tap the menu button (three dots) in the top right",
            "2. Tap 'Add to Home screen'",
            "3. Edit the name if desired",
            "4. Tap 'Add'"
          ]
        };
      } else {
        return {
          title: "Add to Home Screen - Android",
          steps: [
            "1. Look for the menu button (usually three dots or lines)",
            "2. Look for 'Add to Home screen' or similar option",
            "3. Follow the prompts to add the app"
          ],
          note: "Steps may vary depending on your browser."
        };
      }
    }

    return {
      title: "Add to Home Screen",
      steps: ["Please use a mobile device to add this app to your home screen."]
    };
  };

  if (showInstructions) {
    const instructions = getInstructions();
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '400px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <span style={{ fontSize: '24px', marginRight: '8px' }}>🎓</span>
            <h3 style={{ margin: 0, color: '#8b5cf6' }}>{instructions.title}</h3>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            {instructions.steps.map((step, index) => (
              <div key={index} style={{
                padding: '8px 0',
                borderBottom: index < instructions.steps.length - 1 ? '1px solid #f0f0f0' : 'none'
              }}>
                {step}
              </div>
            ))}
          </div>

          {instructions.note && (
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#666',
              marginBottom: '20px'
            }}>
              {instructions.note}
            </div>
          )}

          <button
            onClick={closeInstructions}
            style={{
              width: '100%',
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Got it!
          </button>
        </div>
      </div>
    );
  }

  if (!showPrompt) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: '20px',
      right: '20px',
      backgroundColor: 'white',
      border: '2px solid #8b5cf6',
      borderRadius: '12px',
      padding: '16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      zIndex: 1000,
      maxWidth: '400px',
      margin: '0 auto'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ fontSize: '24px', marginRight: '8px' }}>🎓</span>
        <div>
          <div style={{ fontWeight: 'bold', color: '#8b5cf6', fontSize: '16px' }}>
            Add Practicum AI to Home Screen
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            Get quick access like a native app
          </div>
        </div>
      </div>
      
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleNoThanks}
          style={{
            flex: 1,
            background: 'transparent',
            border: '1px solid #ddd',
            color: '#666',
            padding: '10px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          No thanks
        </button>
        <button
          onClick={handleMaybeLater}
          style={{
            flex: 1,
            background: 'transparent',
            border: '1px solid #8b5cf6',
            color: '#8b5cf6',
            padding: '10px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Maybe later
        </button>
        <button
          onClick={handleYes}
          style={{
            flex: 1,
            background: '#8b5cf6',
            border: 'none',
            color: 'white',
            padding: '10px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '14px'
          }}
        >
          Yes!
        </button>
      </div>
    </div>
  );
};

export default AddToHomeScreenPrompt;